// @flow

import React, { useContext, useState } from 'react';
import { Collapse } from 'react-collapse';

import { Countdown } from 'Components/frameworks/countdown/Countdown';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import Image from 'Components/frameworks/image/Image';
import { truncate } from 'Components/utils/string/string.util';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { TrackClick } from 'Tracking/track-click/TrackClick';
import { OFFER_PUSH, GA_DEFAULT_PAYLOAD } from 'Tracking/constants';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { themes } from './OfferPush.themes';

type Props = CMSOffersPushColumn & {
  id: number,
};

export const OfferPush = (props: Props) => {
  const [isHover, setHover] = useState(false);
  const {
    countdown,
    mediaUrl,
    link,
    subTitle,
    tag,
    theming: { titleColor, subtitleColor },
    title,
    id,
  } = props;
  const { thRoot, thImage, thHeader, thHeaderItem, thTitle, thFooter, thSubTitle } = themes.default(
    {
      titleColor,
      subtitleColor,
      isHover,
    },
  );

  const trackingData = {
    ...GA_DEFAULT_PAYLOAD,
    actionGA: OFFER_PUSH.ACTION,
    labelGA: title,
  };

  const shouldDisplayOffer = !countdown.dateTimeEnd && tag[1]?.text;
  const shouldDisplayCountdown = countdown.dateTimeEnd;
  const { isRTL } = useContext(ResponsiveContext);

  return (
    <article className={thRoot} data-testid="offer-push-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <TrackClick
        className="block relative overflow-hidden"
        as={DynamicLink}
        trackingData={trackingData}
        url={link}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        data-testid={`offer-push-${id}-link`}
      >
        <Image
          altMsg={title}
          className={thImage}
          imageUrl={mediaUrl}
          groupKey="offers-push"
          ratioKey={ratioUtils.XL}
          hasLazyLoading
        />
        <div className={thHeader}>
          {tag[0]?.text && (
            <p
              className={thHeaderItem(tag[0].theming, true)}
              data-testid={`offer-push-${id}-tag-first`}
            >
              <span className="block -mb-px truncate">{tag[0].text.toUpperCase()}</span>
            </p>
          )}
          {shouldDisplayCountdown && (
            <Countdown
              className="mx-1 py-1 leading-one"
              theme="offerPush"
              dataTestId={`offer-push-${id}-countdown`}
              color={countdown.textColor}
              dateTimeEnd={countdown.dateTimeEnd}
              days={countdown.days}
              hours={countdown.hours}
              minutes={countdown.minutes}
              seconds={countdown.seconds}
              bgColor={countdown.backgroundColor}
            />
          )}
          {shouldDisplayOffer && (
            <p
              className={thHeaderItem(tag[1].theming, false)}
              data-testid={`offer-push-${id}-tag-second`}
            >
              <span className="block -mb-px truncate">{tag[1].text.toUpperCase()}</span>
            </p>
          )}
        </div>

        <div className={thFooter}>
          {title && (
            <h3 className={thTitle} data-testid={`offer-push-${id}-title`}>
              {title}
            </h3>
          )}
          {subTitle && (
            <Collapse isOpened={isHover}>
              <p className={thSubTitle} data-testid={`offer-push-${id}-subtitle`}>
                {truncate(subTitle, 115)}
              </p>
            </Collapse>
          )}
        </div>
      </TrackClick>
    </article>
  );
};
